import React, { useState, useRef } from 'react';
import { MainLayout } from '../../components/layout/MainLayout';
import apiClient from '../../api/client';
import toast from 'react-hot-toast';

interface FileWithPartNumber {
  file: File;
  partNumber: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export default function SewingInstructionUpload() {
  const [files, setFiles] = useState<FileWithPartNumber[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const extractPartNumber = (filename: string): string => {
    // Remove .pdf extension and return the filename as part number
    return filename.replace(/\.pdf$/i, '');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validFiles: FileWithPartNumber[] = [];
      const invalidFiles: string[] = [];

      selectedFiles.forEach((file) => {
        if (file.type === 'application/pdf') {
          validFiles.push({
            file,
            partNumber: extractPartNumber(file.name),
            status: 'pending',
          });
        } else {
          invalidFiles.push(file.name);
        }
      });

      if (invalidFiles.length > 0) {
        toast.error(`Skipped non-PDF files: ${invalidFiles.join(', ')}`);
      }

      if (validFiles.length > 0) {
        setFiles(validFiles);
        toast.success(`Selected ${validFiles.length} PDF file(s)`);
      }
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Please select at least one PDF file');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    // Upload files one by one
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];

      // Update status to uploading
      setFiles(prev => prev.map((f, idx) =>
        idx === i ? { ...f, status: 'uploading' } : f
      ));

      try {
        const formData = new FormData();
        formData.append('partNumber', fileItem.partNumber);
        formData.append('file', fileItem.file);

        await apiClient.post('/sewing-instructions/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Update status to success
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'success' } : f
        ));
        successCount++;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Upload failed';
        // Update status to error
        setFiles(prev => prev.map((f, idx) =>
          idx === i ? { ...f, status: 'error', error: errorMessage } : f
        ));
        errorCount++;
      }
    }

    setIsUploading(false);

    // Show summary
    if (successCount > 0 && errorCount === 0) {
      toast.success(`Successfully uploaded ${successCount} file(s)`);
    } else if (successCount > 0 && errorCount > 0) {
      toast(`Uploaded ${successCount} file(s), ${errorCount} failed`, { icon: '⚠️' });
    } else {
      toast.error(`Failed to upload ${errorCount} file(s)`);
    }
  };

  const handleClear = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  return (
    <MainLayout>
      <div className="w-full">
        <div className="bg-white dark:bg-gray-800 shadow">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Upload Sewing Instructions
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Upload PDF sewing instructions - part numbers are automatically extracted from filenames
            </p>
          </div>

          <div className="p-6">
            <div className="max-w-4xl">
              {/* File Input */}
              <div className="mb-6">
                <label htmlFor="file" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select PDF File(s)
                </label>
                <input
                  id="file"
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={handleFileSelect}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 dark:file:bg-gray-600 dark:file:text-gray-200"
                  disabled={isUploading}
                />
              </div>

              {/* Selected Files List */}
              {files.length > 0 && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Selected Files ({files.length})
                    </h3>
                    {!isUploading && (
                      <button
                        onClick={handleClear}
                        className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {files.map((fileItem, index) => (
                      <div
                        key={index}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          fileItem.status === 'success'
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : fileItem.status === 'error'
                            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            : fileItem.status === 'uploading'
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                            : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {fileItem.file.name}
                            </p>
                            {fileItem.status === 'success' && (
                              <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {fileItem.status === 'uploading' && (
                              <svg className="animate-spin w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                            {fileItem.status === 'error' && (
                              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Part Number: <span className="font-medium">{fileItem.partNumber}</span>
                          </p>
                          {fileItem.error && (
                            <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                              Error: {fileItem.error}
                            </p>
                          )}
                        </div>

                        {!isUploading && fileItem.status === 'pending' && (
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="ml-3 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Button */}
              {files.length > 0 && (
                <div className="flex gap-4">
                  <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {isUploading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </span>
                    ) : (
                      `Upload ${files.length} File(s)`
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Instructions */}
            <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg max-w-4xl">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">
                Upload Instructions
              </h3>
              <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1 list-disc list-inside">
                <li>Select one or multiple PDF files at once</li>
                <li>Part numbers are automatically extracted from filenames (without .pdf extension)</li>
                <li>Example: "AC102.pdf" → Part Number: "AC102"</li>
                <li>File size must be 10MB or less per file</li>
                <li>Special characters in filenames will be sanitized automatically ([ replaced with -)</li>
                <li>Uploading a file with an existing part number will overwrite the previous file</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
